import {computed} from "vue";
import {DateTime} from "luxon";
import history from "@/assets/history.json";

const timeZone = history.years[history.latestYear];

function parseNextEventDate() {
  const {nextEvent, latestYear, years} = history;
  if (!nextEvent) return null;
  return DateTime.fromISO(nextEvent, {zone: years[latestYear]}).toJSDate();
}

const keyDatesFiles = import.meta.webpackContext("../assets/years", {
  recursive: true,
  regExp: /keyDates\.json$/
});

function fetchKeyDates() {
  try {
    return keyDatesFiles(`./${history.latestYear}/keyDates.json`);
  } catch {
    return {};
  }
}

function parseEndDate(dates) {
  // accepts "April 15-17", "April 21", or month-only "November" (dates not yet finalized)
  const match = String(dates).match(/^(\w+)(?:\s+(\d+)(?:-(\d+))?)?$/);
  if (!match) return null;
  const [, month, startDay, endDay] = match;
  const base = DateTime.fromFormat(`${month} ${history.latestYear}`, "MMMM yyyy", {zone: timeZone});
  if (!base.isValid) return null;
  const day = endDay || startDay;
  return day ? base.set({day: Number(day)}).endOf("day") : base.endOf("month");
}

function getUpcomingOnlineEvents() {
  const events = Object.entries(fetchKeyDates());
  const onlineEvents = events.filter(([key]) => key.match(/^VIZBI/) && key.match(/online/i));
  return onlineEvents.filter(([, item]) => {
    const endDate = parseEndDate(item.dates);
    return endDate && endDate >= DateTime.now();
  });
}

const nextEventDate = parseNextEventDate();
const upcomingOnlineEvents = getUpcomingOnlineEvents();

export default function useEventState() {
  const isPreEvent = computed(() => !!nextEventDate && nextEventDate > new Date());
  const isPostEvent = computed(() => !isPreEvent.value);
  const isOnlinePreEvent = computed(() => isPostEvent.value && upcomingOnlineEvents.length > 0);
  const isFullyPostEvent = computed(() => isPostEvent.value && upcomingOnlineEvents.length === 0);
  // the next online event's keyDates.json name (the banner shows its dates in the viewer's zone)
  const onlineEventKey = computed(() => upcomingOnlineEvents[0]?.[0] || "");
  const onlineEventLabel = computed(() => {
    const [, item] = upcomingOnlineEvents[0] || [];
    if (!item) return "";
    // bannerDates (keyDates.json): the call-to-action banner's text, e.g. "Nov 3, 18 & 19"
    const label = item.bannerDates || String(item.dates).match(/^(\w+)/)[1];
    return `${label}, ${history.latestYear}`;
  });
  return {
    isPreEvent,
    isPostEvent,
    isOnlinePreEvent,
    isFullyPostEvent,
    onlineEventKey,
    onlineEventLabel,
    nextEventDate
  };
}
